import { merchantOfferExtras } from "@/lib/seo/offer-schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { fetchProducts } from "@/lib/shopify";
import { getCatalogPricing } from "@/lib/pricing/catalog-pricing.functions";

/** Live single-pack price (rupees, 2dp) for structured data. */
function offerPrice(
  pricing: Record<string, Partial<Record<1 | 2 | 3, { price: number; mrp: number | null }>>> | undefined,
  handle: string,
): string | null {
  const pack = pricing?.[handle]?.[1];
  return pack ? pack.price.toFixed(2) : null;
}
import { getFallbackProductList } from "@/lib/fallbackProducts";
import { ProductCard } from "@/components/ProductCard";
import { ProtocolBundle } from "@/components/ProtocolBundle";
import { TrustBar } from "@/components/TrustBar";
import magHero__asset from "@/assets/product-hero/mag-absorb-hero.png.asset.json";
const magHero = magHero__asset.url;
import liverHero__asset from "@/assets/liver-biome-v2/01-hero.webp.asset.json";
const liverHero = liverHero__asset.url;
import ovaHero__asset from "@/assets/product-hero/ova-balance-hero.png.asset.json";
const ovaHero = ovaHero__asset.url;
import { buildFaqPageSchema } from "@/lib/faqSchema";
import { useT } from "@/lib/i18n/provider";
import { useSentence } from "@/lib/i18n/content";
import type { TranslationKey } from "@/lib/i18n/dictionary";

// Concern → product handle map. Drives the /shop?concern= filter chips
// and lets us surface a curated collection page without splitting routes.
const CONCERN_MAP: Record<string, { label: string; handles: string[]; blurb: string }> = {
  all: { label: "All", handles: ["mag-absorb-pro", "liver-biome-plus", "ova-balance"], blurb: "The complete TruWe adult formulary." },
  sleep: { label: "Sleep & stress", handles: ["mag-absorb-pro"], blurb: "5-form magnesium built for deep sleep, calm nerves and muscle recovery." },
  hormones: { label: "PCOS & hormones", handles: ["ova-balance"], blurb: "Gold-standard 40:1 inositol with Quatrefolic® folate for cycle regularity and hormonal acne." },
  gut: { label: "Liver & gut", handles: ["liver-biome-plus"], blurb: "Liposomal curcumin, silymarin, ALA and NAC for daily detox and bloating." },
};
type ConcernKey = keyof typeof CONCERN_MAP;
const CONCERN_ORDER = ["all", "sleep", "hormones", "gut"] as const;

const PRODUCT_IMAGES: Record<string, string> = {
  "mag-absorb-pro": "https://truwe.in/assets/mag-absorb.webp",
  "liver-biome-plus": "https://truwe.in/assets/liver-biome.webp",
  "ova-balance": "https://truwe.in/assets/ova-balance.webp",
};

export const SHOP_FAQ = [
  {
    q: "What makes TruWe formulas different from typical Indian supplements?",
    a: "Every TruWe formula uses research-backed delivery forms, liposomal carriers for fat-soluble actives (curcumin, silymarin, ALA) and chelated minerals (magnesium bisglycinate), paired with Bio-Absorb Matrix, our proprietary phytonutrient blend formulated to support absorption (exact composition proprietary). We publish full doses on the label, third-party lab test every batch, and use research-backed forms only (Aquamin®, Quatrefolic® L-5-MTHF, berberine HCl, 40:1 myo:d-chiro inositol).",
  },
  {
    q: "How do I choose between Mag Absorb Pro, Liver Biome Plus and Ova Balance?",
    a: "Mag Absorb Pro is for sleep, stress and muscle recovery. Liver Biome Plus targets bloating, sluggish digestion and daily detox load. Ova Balance is built for hormonal balance, PCOS, cycle regularity and skin. Take our 60-second quiz for a personalised stack, or stack two together for a complete daily ritual.",
  },
  {
    q: "Are TruWe supplements safe for long-term daily use?",
    a: "Yes. All formulas use food-grade actives at research-backed daily doses, manufactured in an FSSAI-licensed, GMP-certified facility in India. They are vegan, gluten-free, and free from added sugar, artificial colours and synthetic preservatives. Consult your doctor if you are pregnant, nursing or on prescription medication.",
  },
  {
    q: "Do you offer free shipping and easy returns?",
    a: "Yes, free shipping across India on every order, dispatched within 24 hours from our base warehouse. We offer a 3-day return window on unopened bottles and pincode-level delivery estimates at checkout.",
  },
];

const shopSearchSchema = z.object({
  // No .default() here: a default would make TanStack redirect /shop to
  // /shop?concern=all, which turns the canonical shop URL into a 307 and
  // keeps Google from indexing it.
  concern: fallback(z.enum(["all", "sleep", "hormones", "gut"]).optional(), undefined).optional(),
});

export const Route = createFileRoute("/shop")({
  component: ShopPage,
  validateSearch: zodValidator(shopSearchSchema),
  // Prices in the structured data come from the live catalogue published in
  // Admin → Products, never from constants in this file.
  loader: async () => ({ livePricing: await getCatalogPricing().catch(() => ({})) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Buy Health Supplements Online in India | TruWe" },
      {
        name: "description",
        content:
          "Buy premium health supplements in India from Truwe. Explore science-backed, lab-tested, highly absorbable supplements for sleep, detox, energy, and hormonal balance for daily nutrition.",
      },
      { name: "keywords", content: "buy magnesium bisglycinate india, liposomal curcumin, inositol pcos supplement, milk thistle nac, truwe supplements" },
      { property: "og:title", content: "Buy Health Supplements Online in India | TruWe" },
      {
        property: "og:description",
        content:
          "Buy premium health supplements in India from Truwe. Explore science-backed, lab-tested, highly absorbable supplements for sleep, detox, energy, and hormonal balance for daily nutrition.",
      },
      { property: "og:url", content: "https://truwe.in/shop" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/shop" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Buy Health Supplements Online in India | TruWe",
          url: "https://truwe.in/shop",
          description:
            "Buy premium health supplements in India from Truwe. Explore science-backed, lab-tested, highly absorbable supplements for sleep, detox, energy, and hormonal balance for daily nutrition.",
          isPartOf: { "@id": "https://truwe.in/#website" },
          about: { "@id": "https://truwe.in/#organization" },
          mainEntity: {
            "@type": "ItemList",
            name: "TruWe adult formulary",
            numberOfItems: 3,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                url: "https://truwe.in/products/mag-absorb-pro",
                item: {
                  "@type": "Product",
                  name: "Mag Absorb Pro",
                  url: "https://truwe.in/products/mag-absorb-pro",
                  image: PRODUCT_IMAGES["mag-absorb-pro"],
                  description:
                    "Buy the best magnesium supplement in India. TruWe Mag Absorb Pro with 5 magnesium forms including liposomal bisglycinate supports sleep, muscle recovery, and stress relief.",
                  category: "Health & Beauty > Nutritional Supplements",
                  brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                  offers: {
                    "@type": "Offer",
                    url: "https://truwe.in/products/mag-absorb-pro",
                    ...(offerPrice(loaderData?.livePricing, "mag-absorb-pro")
                      ? { price: offerPrice(loaderData?.livePricing, "mag-absorb-pro") }
                      : {}),
                    priceCurrency: "INR",
                    availability: "https://schema.org/InStock",
                    ...merchantOfferExtras(),
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                url: "https://truwe.in/products/liver-biome-plus",
                item: {
                  "@type": "Product",
                  name: "Liver Biome Plus",
                  url: "https://truwe.in/products/liver-biome-plus",
                  image: PRODUCT_IMAGES["liver-biome-plus"],
                  description:
                    "Buy the best liver detox supplement in India. TruWe Liver Biome Plus with liposomal silymarin, NAC & 10B probiotics supports liver health and digestion.",
                  category: "Health & Beauty > Nutritional Supplements",
                  brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                  offers: {
                    "@type": "Offer",
                    url: "https://truwe.in/products/liver-biome-plus",
                    ...(offerPrice(loaderData?.livePricing, "liver-biome-plus")
                      ? { price: offerPrice(loaderData?.livePricing, "liver-biome-plus") }
                      : {}),
                    priceCurrency: "INR",
                    availability: "https://schema.org/InStock",
                    ...merchantOfferExtras(),
                  },
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                url: "https://truwe.in/products/ova-balance",
                item: {
                  "@type": "Product",
                  name: "Ova Balance",
                  url: "https://truwe.in/products/ova-balance",
                  image: PRODUCT_IMAGES["ova-balance"],
                  description:
                    "Buy the best women's health supplement in India. TruWe Ova Balance with 40:1 Inositol + Berberine supports PCOS, hormonal balance, and regular cycles.",
                  category: "Health & Beauty > Nutritional Supplements",
                  brand: { "@type": "Brand", name: "TruWe", "@id": "https://truwe.in/#organization" },
                  offers: {
                    "@type": "Offer",
                    url: "https://truwe.in/products/ova-balance",
                    ...(offerPrice(loaderData?.livePricing, "ova-balance")
                      ? { price: offerPrice(loaderData?.livePricing, "ova-balance") }
                      : {}),
                    priceCurrency: "INR",
                    availability: "https://schema.org/InStock",
                    ...merchantOfferExtras(),
                  },
                },
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "Shop", item: "https://truwe.in/shop" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildFaqPageSchema({
            pageUrl: "https://truwe.in/shop",
            faqs: SHOP_FAQ,
          }),
        ),
      },
    ],
  }),
});

const GOAL_FINDER = [
  { goal: "Better sleep & recovery", handle: "mag-absorb-pro", label: "Mag Absorb Pro" },
  { goal: "Stress & nervous-system reset", handle: "mag-absorb-pro", label: "Mag Absorb Pro" },
  { goal: "PCOS & cycle regularity", handle: "ova-balance", label: "Ova Balance" },
  { goal: "Hormonal acne & mood", handle: "ova-balance", label: "Ova Balance" },
  { goal: "Bloating & sluggish digestion", handle: "liver-biome-plus", label: "Liver Biome Plus" },
  { goal: "Daily detox & liver load", handle: "liver-biome-plus", label: "Liver Biome Plus" },
];

const PRODUCT_DETAILS: Record<string, { tagline: string; hero: string; alt: string; bullets: string[] }> = {
  "mag-absorb-pro": {
    tagline: "Sleep · Stress · Recovery",
    hero: magHero,
    alt: "TruWe Mag Absorb Pro, 5-form magnesium with Aquamin and liposomal bisglycinate",
    bullets: [
      "Liposomal Magnesium Bisglycinate + Aquamin® marine magnesium",
      "Aspartate, Chloride and Gluconate for full-spectrum coverage",
      "Research-backed 440 mg elemental dose per serving",
    ],
  },
      "liver-biome-plus": {
    tagline: "Liver · Gut · Daily detox",
    hero: liverHero,
    alt: "TruWe Liver Biome Plus, liposomal curcumin, silymarin, ALA and NAC with multi-strain probiotic",
    bullets: [
      "Liposomal Curcumin, Silymarin and Alpha Lipoic Acid",
      "NAC 600 mg + 10B CFU multi-strain probiotic",
      "Liposomal delivery for fat-soluble actives",
    ],
  },
  "ova-balance": {
    tagline: "PCOS · Hormones · Skin",
    hero: ovaHero,
    alt: "TruWe Ova Balance, 40:1 myo + d-chiro inositol with Quatrefolic folate",
    bullets: [
      "Gold-standard 40:1 Myo : D-Chiro Inositol ratio",
      "Quatrefolic® L-5-MTHF folate (400 mcg)",
      "Chromium picolinate for insulin sensitivity",
    ],
  },
};

function ShopPage() {
  const t = useT();
  const s18n = useSentence();
  const concern = Route.useSearch().concern ?? "all";
  const { data = [], isLoading } = useQuery({
    queryKey: ["products", 24],
    queryFn: () => fetchProducts(24),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  const liveProducts = data
    .map((p) => p.node)
    .filter((p) => !p.tags.some((t) => t.toLowerCase() === "truwe-kids"));
  const usingFallback = !isLoading && liveProducts.length === 0;
  const allProducts = usingFallback ? getFallbackProductList().map((p) => p.node) : liveProducts;
  const allowedHandles = CONCERN_MAP[concern].handles;
  const products = concern === "all"
    ? allProducts
    : allProducts.filter((p) => allowedHandles.includes(p.handle));

  return (
    <div>
      <TrustBar />
      <div id="protocols" className="scroll-mt-24" />
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-20">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
        <ol className="flex items-center gap-2">
          <li>
            <Link to="/" className="hover:text-brand">{t("shopPage.breadcrumbHome")}</Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-foreground">{t("shopPage.breadcrumbShop")}</li>
        </ol>
      </nav>

      <header className="mb-12 max-w-3xl">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{t("shopPage.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">
          {t("shopPage.h1")}
        </h1>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
          {t("shopPage.introBefore")}{" "}
          <Link to="/ingredients" className="underline decoration-brand/40 underline-offset-4 hover:text-brand">
            Bio-Absorb <em>Matrix</em>
          </Link>
          {" "}{t("shopPage.introAfter")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/quiz"
            className="inline-flex items-center rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-deep"
          >
            {t("shopPage.quizCta")}
          </Link>
          <Link
            to="/ingredients"
            className="inline-flex items-center rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-brand-deep transition hover:border-brand"
          >
            {t("shopPage.ingredientsCta")}
          </Link>
        </div>
      </header>

      {usingFallback && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t("shopPage.fallbackNotice")}
        </div>
      )}

      {/* Concern filter chips, search-param driven, shareable URLs */}
      <div className="mb-6 flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter by concern">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {t("shopPage.shopByConcern")}
        </span>
        {CONCERN_ORDER.map((key) => {
          const active = concern === key;
          return (
            <Link
              key={key}
              to="/shop"
              search={{ concern: key }}
              role="tab"
              aria-selected={active}
              className={`inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                active
                  ? "border-brand bg-brand text-white shadow-sm"
                  : "border-border bg-background text-brand-deep hover:border-brand"
              }`}
            >
              {t(`shopPage.concern.${key}`)}
            </Link>
          );
        })}
      </div>
      <p className="mb-8 max-w-2xl text-sm text-muted-foreground">{t(`shopPage.blurb.${concern}` as TranslationKey)}</p>

      {/* Product grid */}
      <section aria-labelledby="all-products" className="mb-20">
        <h2 id="all-products" className="sr-only">TruWe products, {t(`shopPage.concern.${concern}` as TranslationKey)}</h2>
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse rounded-3xl bg-muted" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-muted-foreground">{t("shopPage.noMatch")} <Link to="/shop" search={{ concern: "all" }} className="text-brand underline">{t("shopPage.showAll")}</Link>.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* The 3-SKU TruWe Protocol bundle, 15% off, checks out as one order */}
      {concern === "all" && (
        <div className="mb-20">
          <ProtocolBundle />
        </div>
      )}

      {/* Shop by need, collection landing pages */}
      <section aria-labelledby="shop-by-need" className="mb-20">
        <h2 id="shop-by-need" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          {t("shopPage.byNeedTitle")}
        </h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t("shopPage.byNeedBody")}
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { slug: "magnesium-supplements", label: "Magnesium supplements", blurb: "Bisglycinate, marine magnesium & more, how to choose." },
            { slug: "sleep-recovery", label: "Sleep & recovery", blurb: "The magnesium-forward approach to evening rituals." },
            { slug: "inositol-supplements", label: "Inositol supplements", blurb: "The 40:1 Myo:D-Chiro ratio, explained." },
            { slug: "pcos-nutrition", label: "PCOS nutrition", blurb: "Inositol, active folate and mineral support." },
            { slug: "liver-gut-support", label: "Liver & gut support", blurb: "NAC, silymarin, curcumin, the gut-liver axis." },
            { slug: "kids-nutrition", label: "Kids nutrition", blurb: "No-added-sugar gummies for growing bodies." },
          ].map((c) => (
            <li key={c.slug}>
              <Link
                to="/collections/$slug"
                params={{ slug: c.slug }}
                className="block h-full rounded-2xl border border-border bg-background px-5 py-4 transition hover:border-brand hover:shadow-sm"
              >
                <p className="text-sm font-semibold text-brand-deep">{c.label} <span className="text-brand">→</span></p>
                <p className="mt-1 text-xs text-muted-foreground">{c.blurb}</p>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Shop by goal, internal-link rich block */}
      <section aria-labelledby="shop-by-goal" className="mb-20 rounded-3xl border border-border bg-card/40 p-8 md:p-12">
        <h2 id="shop-by-goal" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          {t("shopPage.byGoalTitle")}
        </h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t("shopPage.byGoalBody")}
        </p>
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {GOAL_FINDER.map((g) => (
            <li key={g.goal + g.handle}>
              <Link
                to="/products/$handle"
                params={{ handle: g.handle }}
                className="group flex items-center justify-between rounded-2xl border border-border bg-background px-5 py-4 transition hover:border-brand hover:shadow-sm"
              >
                <span className="text-sm font-medium text-brand-deep">{g.goal}</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-brand group-hover:underline">
                  {g.label} →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Detailed formulary, unique long-form crawlable content per SKU */}
      <section aria-labelledby="formulary" className="mb-20">
        <h2 id="formulary" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          {t("shopPage.formularyTitle")}
        </h2>
        <div className="mt-8 space-y-12">
          {(["mag-absorb-pro", "liver-biome-plus", "ova-balance"] as const).map((handle, idx) => {
            const d = PRODUCT_DETAILS[handle];
            const reversed = idx % 2 === 1;
            return (
              <article
                key={handle}
                className={`grid items-center gap-8 md:grid-cols-2 ${reversed ? "md:[&>div:first-child]:order-2" : ""}`}
              >
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{s18n(d.tagline)}</p>
                  <h3 className="mt-2 font-display text-xl font-bold text-brand-deep md:text-2xl">
                    <Link to="/products/$handle" params={{ handle }} className="hover:underline">
                      {handle === "mag-absorb-pro" && "Mag Absorb Pro"}
                      {handle === "liver-biome-plus" && "Liver Biome Plus"}
                      {handle === "ova-balance" && "Ova Balance"}
                    </Link>
                  </h3>
                  <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    {d.bullets.map((b) => (
                      <li key={b} className="flex gap-2">
                        <span aria-hidden className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/products/$handle"
                    params={{ handle }}
                    className="mt-6 inline-flex items-center text-sm font-semibold text-brand hover:underline"
                  >
                    {t("shopPage.readFull")}
                  </Link>
                </div>
                <div className="overflow-hidden rounded-3xl border border-border bg-muted/30">
                  <img
                    src={d.hero}
                    alt={d.alt}
                    loading="lazy"
                    decoding="async"
                    width={800}
                    height={800}
                    className="h-full w-full object-cover"
                  />
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Further reading, internal links to blog for crawl depth */}
      <section aria-labelledby="learn" className="mb-20">
        <h2 id="learn" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          {t("shopPage.learnTitle")}
        </h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          {t("shopPage.learnBody")}
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {[
            { to: "/blog/magnesium-benefits", title: "The 5 magnesium forms that actually work" },
            { to: "/blog/inositol-pcos", title: "Why 40:1 inositol is the gold standard for PCOS" },
            { to: "/blog/curcumin-bioavailability", title: "Curcumin bioavailability: liposomal vs piperine" },
            { to: "/blog/liver-detox-truth", title: "The truth about liver detox supplements" },
            { to: "/blog/berberine-guide", title: "Berberine: dosing, timing and what to expect" },
          ].map((p) => (
            <li key={p.to}>
              <Link
                to={p.to}
                className="block rounded-2xl border border-border bg-background px-5 py-4 text-sm font-medium text-brand-deep transition hover:border-brand"
              >
                {p.title} <span className="text-brand">→</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ, matches FAQPage JSON-LD above */}
      <section aria-labelledby="faq">
        <h2 id="faq" className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
          {t("shopPage.faqTitle")}
        </h2>
        <dl className="mt-8 divide-y divide-border border-y border-border">
          {SHOP_FAQ.map((f) => (
            <div key={f.q} className="py-6">
              <dt className="font-display text-base font-semibold text-brand-deep md:text-lg">{f.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>
      </div>
    </div>
  );
}
